import { ReportSection } from '@/models/Report';
import {
  TableCell,
  TableCellRecord,
  TableColumn,
  TableRow,
} from '@/models/Table';

import { BoundingBox } from '@allenai/pdf-components/src/components/types/boundingBox'; // eslint-disable-line import/no-unresolved

export enum WidgetType {
  DATA_VOYAGER = 'DATA_VOYAGER',
  READER = 'READER',
  TABLE = 'TABLE',
  TABLE_RECOMMENDATIONS = 'TABLE_RECOMMENDATIONS',
  PAPER_FINDER = 'PAPER_FINDER',
  REPORT = 'REPORT',
}

export interface WidgetDataBase {
  type: WidgetType;
}

export interface ReaderWidgetData extends WidgetDataBase {
  type: WidgetType.READER;
  corpusId: number;
  highlights?: BoundingBox[];
}

export interface NewTableWidget extends Omit<TableWidget, 'id'> {}
export interface TableWidget extends WidgetDataBase {
  type: WidgetType.TABLE | WidgetType.TABLE_RECOMMENDATIONS;
  id: string;
  title: string;
  rows: TableRow[];
  columns: TableColumn[];
  cells: TableCellRecord;
  isLoading: boolean;
}

export type TableWidgetData = NewTableWidget | TableWidget;

export interface PaperFinderWidgetData extends WidgetDataBase {
  type: WidgetType.PAPER_FINDER;
  id: string;
}

export interface ReportWidgetData extends WidgetDataBase {
  type: WidgetType.REPORT;
  id: string;
  sections: ReportSection[];
  query?: string;
  // TODO: Add fields
}

export interface DataVoyagerWidgetData extends WidgetDataBase {
  type: WidgetType.DATA_VOYAGER;
  id: string;
  logs: string;
}

export type WidgetData =
  | DataVoyagerWidgetData
  | ReaderWidgetData
  | TableWidgetData
  | PaperFinderWidgetData
  | ReportWidgetData;

export enum WidgetEvent {
  WIDGET_OPEN = 'widget_open',
  WIDGET_CLOSE = 'widget_close',
}

export type WidgetId = string;

/**
 * Removes fields from reportData that are not relevant to users
 * @param data The original report data
 * @returns A new object with irrelevant fields removed
 */
export const removeIrrelevantFields = (data: ReportWidgetData | null): any => {
  if (!data) return null;

  // Create a deep copy to avoid mutating the original data
  const cleanedData = JSON.parse(JSON.stringify(data));

  // Remove section -> table -> isLoading
  cleanedData.sections.forEach((section: any) => {
    if (section.table && 'isLoading' in section.table) {
      const { isLoading, ...rest } = section.table;
      section.table = rest;
    }

    // Remove section -> table -> columns -> tools, isMetadata
    if (section.table?.columns) {
      section.table.columns = section.table.columns.map((column: any) => {
        if (column && 'tools' in column) {
          const { tools, isMetadata, ...rest } = column;
          return rest;
        }
        return column;
      });
    }

    // Remove section -> table -> rows -> isSelected
    if (section.table?.rows) {
      section.table.rows = section.table.rows.map((row: any) => {
        if (row && 'isSelected' in row) {
          const { isSelected, ...rest } = row;
          return rest;
        }
        return row;
      });
    }

    // Remove section -> table -> cells -> isLoading, metadata, editedValue, editedByUUID
    if (section.table?.cells) {
      (Object.entries(section.table.cells) as [string, TableCell][]).forEach(
        ([cellKey, cellValue]) => {
          if (cellValue) {
            const { isLoading, metadata, editedValue, editedByUUID, ...rest } =
              cellValue;
            section.table.cells[cellKey] = rest;
          }
        },
      );
    }
  });

  return cleanedData;
};
